import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, RefreshCw, FileText, User, Calendar } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import moment from 'moment';

ModuleRegistry.registerModules([AllCommunityModule]);

const ReportLogViewer = ({ authenticatedFetch, user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const gridRef = useRef();
  
  const API_BASE = useMemo(() => {
    const base = process.env.REACT_APP_API_BASE_URL || '';
    return `${base}/api/reports`;
  }, []);
  
  // Memoize fetchLogs with useCallback
  const fetchLogs = useCallback(async () => {
    console.log('fetchLogs called', { filter, userId: user?.id, API_BASE, hasAuthFetch: !!authenticatedFetch });
    
    if (!authenticatedFetch) {
      console.error('authenticatedFetch is not available');
      setError('Authentication function not available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let url = API_BASE;
      
      if (filter === 'MY_REPORTS' && user?.id) {
        url = `${API_BASE}/user/${user.id}`;
      } else if (filter !== 'ALL') {
        url = `${API_BASE}/status/${filter}`;
      }
      
      url += '?limit=100';
      
      console.log('Fetching from URL:', url);
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Fetched logs:', data?.length || 0, 'records');
      setLogs(Array.isArray(data) ? data : []);
      setIsInitialized(true);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message || 'Failed to fetch logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, filter, user?.id, authenticatedFetch]);
  
  const searchLogs = useCallback(async () => {
    if (!searchTerm) {
      fetchLogs();
      return;
    }
    
    if (!authenticatedFetch) {
      setError('Authentication function not available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const url = `${API_BASE}/search?keyword=${encodeURIComponent(searchTerm)}&maxResults=100`;
      console.log('Searching with URL:', url);
      
      const response = await authenticatedFetch(url, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Failed to search logs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Search results:', data?.length || 0, 'records');
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error searching logs:', err);
      setError(err.message || 'Failed to search logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, API_BASE, authenticatedFetch, fetchLogs]);
  
  // Initial load
  useEffect(() => {
    console.log('Component mounted/filter changed');
    if (authenticatedFetch) {
      fetchLogs();
    } else {
      console.warn('authenticatedFetch not available on mount');
    }
  }, [filter, authenticatedFetch, fetchLogs]);
  
  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && authenticatedFetch) {
      console.log('Setting up auto-refresh');
      const interval = setInterval(() => {
        console.log('Auto-refresh triggered');
        fetchLogs();
      }, 10000);
      return () => {
        console.log('Cleaning up auto-refresh');
        clearInterval(interval);
      };
    }
  }, [autoRefresh, authenticatedFetch, fetchLogs]);
  
  const StatusCellRenderer = useCallback((props) => {
    const status = props.value?.toUpperCase();
    let icon, badgeClass;
    
    switch (status) {
      case 'SUCCESS':
        icon = <CheckCircle className="text-success" size={16} />;
        badgeClass = 'badge bg-success';
        break;
      case 'FAILED':
        icon = <XCircle className="text-danger" size={16} />;
        badgeClass = 'badge bg-danger';
        break;
      default:
        icon = <FileText className="text-secondary" size={16} />;
        badgeClass = 'badge bg-secondary';
    }
    
    return (
      <div className="d-flex align-items-center gap-2" style={{ height: '100%' }}>
        {icon}
        <span className={badgeClass}>{props.value}</span>
      </div>
    );
  }, []);

  const ReportIdCellRenderer = useCallback((props) => {
    return (
      <span className="badge bg-light text-dark">
        <FileText size={12} className="me-1" />
        #{props.value}
      </span>
    );
  }, []);

  const UserIdCellRenderer = useCallback((props) => {
    return (
      <span className="badge bg-info text-dark">
        <User size={12} className="me-1" />
        {props.value}
      </span>
    );
  }, []);

  const DateCellRenderer = useCallback((props) => {
    return (
      <span className="text-muted small">
        <Calendar size={12} className="me-1" />
        {moment(props.value).format('YYYY-MM-DD HH:mm:ss')}
      </span>
    );
  }, []);

  const DurationCellRenderer = useCallback((props) => {
  const seconds = props.value || 0;
  return (
    <span className="flex items-center font-semibold text-gray-800">
      ⏱ {seconds} sec
    </span>
  );
}, []);

  const showLogDetails = useCallback((log) => {
    setSelectedLog(log);
  }, []);

  const ActionsCellRenderer = useCallback((props) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={() => showLogDetails(props.data)}
        >
          View Details
        </button>
      </div>
    );
  }, [showLogDetails]);

  const parseParams = (paramsString) => {
    try {
      return JSON.parse(paramsString);
    } catch (e) {
      return null;
    }
  };

  const formatParamValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const closeLogDetails = useCallback(() => {
    setSelectedLog(null);
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: StatusCellRenderer,
      width: 150,
      filter: 'agTextColumnFilter',
      sortable: true
    },
    {
      headerName: 'Report ID',
      field: 'reportId',
      cellRenderer: ReportIdCellRenderer,
      width: 120,
      filter: 'agNumberColumnFilter',
      sortable: true,
      sort: 'desc',
    },
    {
      headerName: 'User Email',
      field: 'username',
      cellRenderer: UserIdCellRenderer,
      width: 120,
      filter: 'agTextColumnFilter',
      sortable: true
    },
    {
      headerName: 'IP Address',
      field: 'ip',
      cellRenderer: StatusCellRenderer,
      width: 120,
      filter: 'agTextColumnFilter',
      sortable: true
    },
    {
      headerName: 'Created At',
      field: 'createdAt',
      cellRenderer: DateCellRenderer,
      width: 200,
      filter: 'agDateColumnFilter',
      sortable: true,
    },
    {
      headerName: 'Duration (Seconds)',
      field: 'durationSeconds',
      cellRenderer: DurationCellRenderer,
      width: 200,
      filter: 'agNumberColumnFilter',
      sortable: true,
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      width: 130,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' }
    }
  ], [StatusCellRenderer, ReportIdCellRenderer, UserIdCellRenderer, DateCellRenderer, DurationCellRenderer, ActionsCellRenderer]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: false
  }), []);

  // StatusBar removed - requires AG Grid Enterprise

  const onGridReady = useCallback((params) => {
    console.log('Grid ready', 'Row count:', params.api.getDisplayedRowCount());
    // Store the grid API ref
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
    }
    setTimeout(() => {
      if (params.api) {
        params.api.sizeColumnsToFit();
        console.log('Columns sized, Row count:', params.api.getDisplayedRowCount());
      }
    }, 100);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current?.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Resize grid when logs change
  useEffect(() => {
    console.log('Logs changed, count:', logs.length);
    if (gridRef.current?.api) {
      console.log('Grid API available, current row count:', gridRef.current.api.getDisplayedRowCount());
      // Force grid to refresh and resize
      setTimeout(() => {
        if (gridRef.current?.api) {
          gridRef.current.api.sizeColumnsToFit();
          // Redraw rows to ensure they appear
          gridRef.current.api.redrawRows();
        }
      }, 150);
    } else {
      console.log('Grid API not available yet');
    }
  }, [logs]);

  const handleRefreshClick = useCallback(() => {
    console.log('Manual refresh clicked');
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = useCallback((newFilter) => {
    console.log('Filter changed to:', newFilter);
    setFilter(newFilter);
  }, []);

  const handleAutoRefreshToggle = useCallback(() => {
    setAutoRefresh(prev => !prev);
  }, []);
  
  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h2 className="card-title mb-4">Report Logs</h2>
          
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-auto">
              <div className="btn-group" role="group">
                {['ALL', 'MY_REPORTS', 'SUCCESS', 'FAILED'].map((filterOption) => (
                  <button
                    key={filterOption}
                    type="button"
                    onClick={() => handleFilterChange(filterOption)}
                    className={`btn ${filter === filterOption ? 'btn-primary' : 'btn-outline-primary'}`}
                  >
                    {filterOption === 'MY_REPORTS' ? 'My Reports' : filterOption}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="col-12 col-md-auto">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-success"
                  onClick={handleRefreshClick}
                  disabled={loading}
                >
                  <RefreshCw size={16} className={loading ? 'spinner-border spinner-border-sm' : ''} />
                  {' '}{loading ? 'Loading...' : 'Refresh'}
                </button>
                
                <button
                  className={`btn ${autoRefresh ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={handleAutoRefreshToggle}
                >
                  Auto {autoRefresh ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-muted small mb-2">
            {loading ? 'Loading...' : `Showing ${logs.length} report logs`}
          </div>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
          <XCircle size={20} className="me-2" />
          <div>
            <strong>Error loading logs:</strong> {error}
          </div>
        </div>
      )}
      
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="ag-theme-alpine" style={{ width: '100%', height: '600px' }}>
            <AgGridReact
              ref={gridRef}
              rowData={logs}
              onGridReady={onGridReady}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              domLayout="normal"
              suppressPaginationPanel={false}
              paginationAutoPageSize={false}
              loading={loading}
              overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading report logs...</span>'
              overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No report logs found</span>'
            />
          </div>
        </div>
      </div>

      {/* Modal for Log Details */}
      {selectedLog && (
        <div 
          className="modal show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} 
          onClick={closeLogDetails}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Report Details - #{selectedLog.reportId}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeLogDetails}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6 className="text-muted">Status</h6>
                  <span className={`badge ${selectedLog.status?.toUpperCase() === 'SUCCESS' ? 'bg-success' : selectedLog.status?.toUpperCase() === 'FAILED' ? 'bg-danger' : 'bg-secondary'}`}>
                    {selectedLog.status}
                  </span>
                </div>

                <div className="mb-3">
                  <h6 className="text-muted">User Email</h6>
                  <p>{selectedLog.username}</p>
                </div>

                <div className="mb-3">
                  <h6 className="text-muted">Duration (Seconds)</h6>
                  <p>{selectedLog.durationSeconds}</p>
                </div>


                <div className="mb-3">
                  <h6 className="text-muted">Parameters</h6>
                  <div className="card bg-light">
                    <div className="card-body">
                      {(() => {
                        const params = parseParams(selectedLog.params);
                        if (!params) {
                          return <p className="font-monospace small mb-0">{selectedLog.params}</p>;
                        }
                        
                        return (
                          <div>
                            {params.parameters && params.parameters.length > 0 && (
                              <div className="mb-2">
                                <strong>Search Parameters:</strong>
                                <ul className="list-unstyled ms-3 mt-1">
                                  {params.parameters.map((param, idx) => (
                                    <li key={idx} className="small">
                                      <strong>{param.name}:</strong> {formatParamValue(param.value)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {params.limit && (
                              <div className="small">
                                <strong>Limit:</strong> {params.limit}
                              </div>
                            )}
                            
                            {params.loadAll !== undefined && (
                              <div className="small">
                                <strong>Load All:</strong> {params.loadAll ? 'Yes' : 'No'}
                              </div>
                            )}
                            
                            {params.sessionId && (
                              <div className="small">
                                <strong>Session ID:</strong> {params.sessionId}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeLogDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportLogViewer;